import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock } from "lucide-react";

/**
 * CEO Report v2 — single contiguous scrollable Detailed AI Readiness Report.
 * Replaces the 8-tab subtab layout in the admin assessment detail view.
 *
 * Props:
 *   result          — assessment_results doc (required)
 *   prospectInfo    — { company_name, contact_name, contact_email }
 *   tenantId        — string
 *   onConfirmScope  — async (topThreeWorkflows) => void
 *
 * The component renders ONLY the report body (sections + anchor nav).
 * The chrome bar (Print / Export PDF / Email Report) is rendered by the
 * parent AssessmentReport — we just provide hooks to render the new layout.
 */

const CSS = `
:root {
  --blue:#3B4FE4; --blue-light:#EEF0FD; --blue-dark:#1E2A8A;
  --amber:#C97B1A; --amber-light:#FEF3DC; --amber-bg:#FFFBF3;
  --green:#1A6B42; --green-light:#E4F3EC;
  --red:#B83232; --red-light:#FDECEA;
  --gold:#E8A020; --ink:#0F1117; --ink2:#2D3142; --ink3:#5C6070; --ink4:#9499A8;
  --rule:rgba(0,0,0,0.07); --rule2:rgba(0,0,0,0.12);
  --bg:#FAFAF8; --bg-card:#FFFFFF;
  --radius:10px; --radius-lg:16px;
}
.ceo2 *, .ceo2 *::before, .ceo2 *::after { box-sizing: border-box; }
.ceo2 { font-family: 'DM Sans', 'Segoe UI', system-ui, -apple-system, sans-serif; color: var(--ink); background: var(--bg); }
.ceo2 .anchor-nav { position: sticky; top: 0; z-index: 12; background: rgba(255,255,255,0.97); backdrop-filter: blur(8px); border-bottom: 1px solid var(--rule); padding: 10px 0; margin-bottom: 24px; display: flex; gap: 4px; overflow-x: auto; scrollbar-width: none; }
.ceo2 .anchor-nav::-webkit-scrollbar { display: none; }
.ceo2 .anchor-link { padding: 8px 14px; font-size: 12px; font-weight: 600; color: var(--ink3); border-radius: 8px; cursor: pointer; white-space: nowrap; transition: all .15s; border-bottom: 2px solid transparent; }
.ceo2 .anchor-link:hover { color: var(--ink); background: var(--blue-light); }
.ceo2 .anchor-link.active { color: var(--blue); border-bottom-color: var(--blue); background: rgba(59,79,228,0.05); }
.ceo2 .hero { padding: 40px 0 32px; border-bottom: 1px solid var(--rule); margin-bottom: 32px; }
.ceo2 .hero-eyebrow { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.10em; color: var(--blue); margin-bottom: 10px; }
.ceo2 .hero-title { font-size: 28px; font-weight: 700; letter-spacing: -0.01em; margin-bottom: 6px; }
.ceo2 .hero-meta { font-size: 13px; color: var(--ink3); margin-bottom: 28px; }
.ceo2 .score-band { display: grid; grid-template-columns: 320px 1fr; gap: 32px; align-items: start; }
.ceo2 .score-primary { padding: 24px; background: var(--bg-card); border: 1px solid var(--rule2); border-radius: var(--radius-lg); }
.ceo2 .score-num { font-size: 56px; font-weight: 700; color: var(--ink); line-height: 1; }
.ceo2 .score-denom { font-size: 18px; color: var(--ink4); }
.ceo2 .score-label { font-size: 13px; color: var(--ink3); margin: 8px 0 12px; }
.ceo2 .score-verdict { display: inline-block; padding: 5px 12px; background: var(--amber-light); color: var(--amber); font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; border-radius: 999px; }
.ceo2 .score-kpis { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
.ceo2 .kpi-card { padding: 16px; background: var(--bg-card); border: 1px solid var(--rule2); border-radius: var(--radius); }
.ceo2 .kpi-label { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.10em; color: var(--ink4); margin-bottom: 8px; }
.ceo2 .kpi-value { font-size: 20px; font-weight: 700; color: var(--ink); margin-bottom: 4px; }
.ceo2 .kpi-sub { font-size: 11px; color: var(--ink3); margin-bottom: 8px; line-height: 1.4; }
.ceo2 .kpi-pill { display: inline-block; padding: 3px 9px; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; border-radius: 999px; }
.ceo2 .pill-red    { background: var(--red-light);   color: var(--red); }
.ceo2 .pill-green  { background: var(--green-light); color: var(--green); }
.ceo2 .pill-amber  { background: var(--amber-light); color: var(--amber); }
.ceo2 .pill-blue   { background: var(--blue-light);  color: var(--blue); }
.ceo2 .pill-dark   { background: rgba(15,17,23,0.08); color: var(--ink2); }
.ceo2 .section { margin-bottom: 48px; scroll-margin-top: 110px; animation: fadeUp 0.45s ease both; }
.ceo2 .section + .section { padding-top: 32px; border-top: 1px solid var(--rule); }
.ceo2 .section-eyebrow { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.12em; color: var(--blue); margin-bottom: 6px; }
.ceo2 .section-title { font-size: 22px; font-weight: 700; letter-spacing: -0.01em; margin-bottom: 6px; }
.ceo2 .section-sub { font-size: 13px; color: var(--ink3); margin-bottom: 24px; }
.ceo2 .verdict-block { padding: 20px 22px; background: var(--amber-bg); border-left: 4px solid var(--amber); border-radius: 0 var(--radius) var(--radius) 0; margin-bottom: 24px; }
.ceo2 .verdict-title { font-size: 13px; font-weight: 700; color: var(--amber); margin-bottom: 8px; text-transform: uppercase; letter-spacing: 0.06em; }
.ceo2 .verdict-body { font-size: 14px; line-height: 1.6; color: var(--ink2); }
.ceo2 .signal-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
.ceo2 .signal-card { padding: 18px; border-radius: var(--radius); border: 1px solid var(--rule2); }
.ceo2 .sc-red { background: var(--red-light); border-color: rgba(184,50,50,0.20); }
.ceo2 .sc-green { background: var(--green-light); border-color: rgba(26,107,66,0.20); }
.ceo2 .signal-icon { font-size: 18px; margin-bottom: 8px; }
.ceo2 .signal-eyebrow { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.10em; margin-bottom: 4px; }
.ceo2 .sc-red .signal-eyebrow { color: var(--red); }
.ceo2 .sc-green .signal-eyebrow { color: var(--green); }
.ceo2 .signal-title { font-size: 14px; font-weight: 700; color: var(--ink); margin-bottom: 6px; }
.ceo2 .signal-desc { font-size: 12px; color: var(--ink3); line-height: 1.5; }
.ceo2 .pillar-table { background: var(--bg-card); border: 1px solid var(--rule2); border-radius: var(--radius-lg); overflow: hidden; margin-bottom: 24px; }
.ceo2 .pillar-row { display: grid; grid-template-columns: 220px 1fr 90px 130px; gap: 18px; align-items: center; padding: 16px 22px; border-bottom: 1px solid var(--rule); }
.ceo2 .pillar-row:last-child { border-bottom: none; }
.ceo2 .pillar-row-head { background: rgba(15,17,23,0.025); padding: 10px 22px; }
.ceo2 .pillar-col-label { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.10em; color: var(--ink4); }
.ceo2 .pillar-name { font-size: 14px; font-weight: 700; }
.ceo2 .pillar-so-what { font-size: 12px; color: var(--ink3); margin-top: 2px; }
.ceo2 .pillar-bar-shell { height: 8px; background: rgba(15,17,23,0.06); border-radius: 999px; overflow: hidden; margin-bottom: 6px; }
.ceo2 .pillar-bar-fill { height: 100%; border-radius: 999px; }
.ceo2 .pillar-explain { font-size: 11.5px; color: var(--ink3); line-height: 1.4; }
.ceo2 .pillar-score { font-size: 16px; font-weight: 700; text-align: right; }
.ceo2 .pillar-score span { font-size: 12px; color: var(--ink4); font-weight: 500; }
.ceo2 .pillar-status { text-align: right; }
.ceo2 .ge-band { display: grid; grid-template-columns: 1fr 1fr 1fr; background: var(--bg-card); border: 1px solid var(--rule2); border-radius: var(--radius-lg); margin-bottom: 20px; overflow: hidden; }
.ceo2 .ge-cell { padding: 22px; border-right: 1px solid var(--rule); }
.ceo2 .ge-cell:last-child { border-right: none; }
.ceo2 .ge-cell-label { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.10em; color: var(--ink4); margin-bottom: 10px; }
.ceo2 .ge-num { font-size: 32px; font-weight: 700; line-height: 1; margin-bottom: 8px; color: var(--ink); }
.ceo2 .ge-interp { font-size: 12.5px; color: var(--ink3); line-height: 1.55; }
.ceo2 .scale-legend { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; }
.ceo2 .scale-tile { padding: 10px 12px; background: var(--bg-card); border: 1px solid var(--rule2); border-radius: var(--radius); font-size: 11px; }
.ceo2 .scale-tile strong { display: block; font-size: 14px; font-weight: 700; }
.ceo2 .insight { padding: 16px 18px; background: var(--blue-light); border-radius: var(--radius); font-size: 13px; color: var(--ink2); line-height: 1.55; margin-top: 18px; }
.ceo2 .insight strong { color: var(--blue-dark); }
.ceo2 .wf-list { display: flex; flex-direction: column; gap: 10px; margin-bottom: 24px; }
.ceo2 .wf-item { display: grid; grid-template-columns: 40px 1fr auto; gap: 16px; padding: 16px 18px; background: var(--bg-card); border: 1px solid var(--rule2); border-radius: var(--radius); align-items: start; }
.ceo2 .wf-item.wf-ready-card { border: 1.5px solid var(--green); }
.ceo2 .wf-num { width: 36px; height: 36px; border-radius: 999px; background: var(--blue); color: white; display: flex; align-items: center; justify-content: center; font-size: 14px; font-weight: 700; }
.ceo2 .wf-name { font-size: 14px; font-weight: 700; }
.ceo2 .wf-meta-row { font-size: 11px; color: var(--ink4); margin-top: 2px; }
.ceo2 .wf-desc { font-size: 13px; color: var(--ink3); line-height: 1.55; margin-top: 6px; }
.ceo2 .wf-badges { display: flex; flex-direction: column; gap: 6px; align-items: flex-end; }
.ceo2 .wb-ready    { background: var(--green-light); color: var(--green); padding: 4px 10px; font-size: 10px; font-weight: 700; text-transform: uppercase; border-radius: 999px; letter-spacing: 0.05em; }
.ceo2 .wb-needs    { background: var(--amber-light); color: var(--amber); padding: 4px 10px; font-size: 10px; font-weight: 700; text-transform: uppercase; border-radius: 999px; letter-spacing: 0.05em; }
.ceo2 .wb-blocked  { background: var(--red-light);   color: var(--red);   padding: 4px 10px; font-size: 10px; font-weight: 700; text-transform: uppercase; border-radius: 999px; letter-spacing: 0.05em; }
.ceo2 .wb-agent    { background: var(--blue-light);  color: var(--blue);  padding: 4px 10px; font-size: 10px; font-weight: 700; text-transform: uppercase; border-radius: 999px; letter-spacing: 0.05em; }
.ceo2 .wb-custom   { background: rgba(15,17,23,0.06); color: var(--ink2); padding: 4px 10px; font-size: 10px; font-weight: 700; text-transform: uppercase; border-radius: 999px; letter-spacing: 0.05em; }
.ceo2 .timeline { display: flex; flex-direction: column; gap: 22px; margin-bottom: 28px; }
.ceo2 .tl-row { display: grid; grid-template-columns: 120px 1fr; gap: 24px; position: relative; }
.ceo2 .tl-rail { font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; color: var(--ink2); text-align: right; padding-right: 18px; padding-top: 6px; border-right: 2px solid var(--rule); position: relative; }
.ceo2 .tl-dot { position: absolute; right: -8px; top: 8px; width: 14px; height: 14px; border-radius: 999px; background: var(--blue); border: 3px solid var(--bg); }
.ceo2 .tl-actions { display: flex; flex-direction: column; gap: 10px; padding-left: 4px; }
.ceo2 .tl-action { display: grid; grid-template-columns: 28px 1fr; gap: 10px; align-items: start; }
.ceo2 .tl-num { width: 22px; height: 22px; border-radius: 999px; background: var(--blue); color: white; font-size: 11px; font-weight: 700; display: flex; align-items: center; justify-content: center; margin-top: 1px; }
.ceo2 .tl-text { font-size: 13px; color: var(--ink2); line-height: 1.55; }
.ceo2 .next-step-cta { background: var(--ink); color: white; padding: 26px 28px; border-radius: var(--radius-lg); display: grid; grid-template-columns: 1fr auto; gap: 22px; align-items: center; }
.ceo2 .ns-eyebrow { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.14em; color: rgba(255,255,255,0.55); margin-bottom: 8px; }
.ceo2 .ns-title { font-size: 20px; font-weight: 700; margin-bottom: 8px; }
.ceo2 .ns-body { font-size: 13px; color: rgba(255,255,255,0.78); line-height: 1.55; }
.ceo2 .ns-btn { padding: 14px 26px; background: var(--blue); color: white; border: none; border-radius: var(--radius); font-size: 14px; font-weight: 700; cursor: pointer; transition: background .15s; white-space: nowrap; }
.ceo2 .ns-btn:hover { background: var(--blue-dark); }
.ceo2 .locked-card { padding: 28px; background: var(--bg-card); border: 1px dashed var(--rule2); border-radius: var(--radius-lg); text-align: center; }
.ceo2 .locked-icon { font-size: 28px; margin-bottom: 10px; }
.ceo2 .locked-title { font-size: 16px; font-weight: 700; margin-bottom: 8px; }
.ceo2 .locked-body { font-size: 13px; color: var(--ink3); line-height: 1.5; max-width: 540px; margin: 0 auto 14px; }
.ceo2 .locked-btn { padding: 10px 20px; background: var(--blue); color: white; border-radius: var(--radius); font-size: 13px; font-weight: 700; border: none; cursor: pointer; }
@keyframes fadeUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
@media (max-width: 1024px) {
  .ceo2 .score-band { grid-template-columns: 1fr; }
  .ceo2 .score-kpis { grid-template-columns: repeat(2, 1fr); }
  .ceo2 .ge-band { grid-template-columns: 1fr 1fr; }
  .ceo2 .ge-cell:nth-child(3) { grid-column: 1 / -1; border-top: 1px solid var(--rule); border-right: none; }
  .ceo2 .pillar-row { grid-template-columns: 160px 1fr 70px 110px; }
}
@media (max-width: 768px) {
  .ceo2 .hero-title { font-size: 22px; }
  .ceo2 .section-title { font-size: 18px; }
  .ceo2 .score-num { font-size: 40px; }
  .ceo2 .ge-band { grid-template-columns: 1fr; }
  .ceo2 .ge-cell { border-right: none; border-bottom: 1px solid var(--rule); }
  .ceo2 .signal-grid { grid-template-columns: 1fr; }
  .ceo2 .pillar-row { grid-template-columns: 1fr 90px; }
  .ceo2 .pillar-row > :nth-child(2) { display: none; }
  .ceo2 .wf-item { grid-template-columns: 1fr; }
  .ceo2 .wf-num { width: 28px; height: 28px; font-size: 12px; }
  .ceo2 .wf-badges { flex-direction: row; align-items: flex-start; }
  .ceo2 .scale-legend { grid-template-columns: 1fr 1fr; }
  .ceo2 .next-step-cta { grid-template-columns: 1fr; }
  .ceo2 .tl-row { grid-template-columns: 90px 1fr; }
}
@media print {
  .ceo2 .anchor-nav { position: static; }
  .ceo2 .section { animation: none; opacity: 1; transform: none; }
  .ceo2 #workflows { page-break-before: always; }
  .ceo2 .next-step-cta { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
}
`;

// ── helpers ────────────────────────────────────────

const barColor = (score) => score < 1.75 ? "#E05A2B" : score <= 2.5 ? "#E8A020" : "#2E9E65";
const priorityPill = (score) => {
  if (score < 1.75) return { label: "Significant Gap", cls: "pill-red" };
  if (score <= 2.5) return { label: "Improve", cls: "pill-amber" };
  return { label: "Good", cls: "pill-amber" };
};

const PILLAR_SO_WHAT = (name, score) => {
  const map = {
    "Business & AI Strategy":       score < 2.0 ? "Fix first — blocks everything else"   : "Align AI goals to business outcomes",
    "Measurement & Value":          score < 2.0 ? "Define KPIs before any deployment"    : "Strengthen tracking and reporting",
    "Org Readiness & Change":       score < 2.0 ? "Build internal champions now"         : "Train teams, reduce adoption friction",
    "Process & Use-Case Readiness": score < 2.0 ? "Document workflows before automating" : "Refine process documentation",
    "Technology & Data":            score < 2.0 ? "Data quality needs attention first"   : "Solid — maintain and integrate",
    "Governance & Security":        score > 2.2 ? "Your strongest asset — build on it"   : "Establish AI use policies",
  };
  return map[name] ?? "Review and strengthen";
};

const PILLAR_EXPLAIN = (name) => ({
  "Business & AI Strategy":       "How clearly AI investment is tied to measurable business goals.",
  "Measurement & Value":          "How rigorously outcomes, KPIs, and ROI are tracked.",
  "Org Readiness & Change":       "How prepared people are to adopt new AI-enabled workflows.",
  "Process & Use-Case Readiness": "How well key workflows are documented and ready for automation.",
  "Technology & Data":            "Data quality, system integration, and infrastructure readiness.",
  "Governance & Security":        "Policies and controls covering AI use, data, and risk.",
}[name] || "Drives readiness for AI deployment.");

const generateVerdict = (org, score, lowest, readyWfs) => {
  const position = score >= 2.5 ? "a strong" : score >= 2.0 ? "a good" : "a developing";
  const interpretation = score >= 2.5
    ? "your organization has solid operational foundations and clear areas to optimize"
    : score >= 2.0
      ? "your organization has the operational foundation AI needs, but has not yet aligned strategy and measurement around it"
      : "your organization has meaningful gaps to close before AI can be deployed at scale, but targeted pilots are possible now";
  const close = readyWfs.length > 0
    ? `${readyWfs[0]?.name || "Your highest-priority workflow"} is ready to deploy today and can begin generating value within weeks.`
    : "Begin with workflow preparation to unlock your first deployments.";
  return `${org} is in ${position} position to begin AI implementation. A score of ${score} out of 4.0 means ${interpretation}. The ${lowest?.name || "lowest-scoring"} gap (${lowest?.score || "—"}/4) is the primary area to address — closing it creates compounding value for every future AI initiative. ${close}`;
};

const generateActions = (workflows, lowestPillar) => {
  const ready = workflows.filter((w) => w.status === "Ready").sort((a, b) => (a.ai_priority || 99) - (b.ai_priority || 99));
  const needsPrep = workflows.filter((w) => w.status === "Needs Prep").sort((a, b) => (a.ai_priority || 99) - (b.ai_priority || 99));
  const blocked = workflows.filter((w) => w.status === "Blocked").sort((a, b) => (a.ai_priority || 99) - (b.ai_priority || 99));

  return {
    phase1: [
      ready[0] ? `Deploy ${ready[0].name} — this workflow is ready now and requires no infrastructure prep. Assign ${ready[0].owner || "an internal owner"} as the day-one owner and define a 30-day success metric.` : `Begin process documentation for ${needsPrep[0]?.name || "your highest-priority workflow"} to unlock your first deployment.`,
      ready[1] ? `Deploy ${ready[1].name}. Assign ${ready[1].owner || "an owner"} and set a baseline for current performance to measure against.` : `Audit data environment for ${needsPrep[0]?.name || "your highest-priority workflow"} — identify integration points and data quality gaps.`,
      `Convene a leadership session to define the top 3 business outcomes AI should drive. This directly addresses the ${lowestPillar?.name || "biggest"} gap and becomes the filter for every future AI investment decision.`,
    ],
    phase2: [
      "Review Week 4 metrics from live workflows. Document what is working, where staff are hesitating, and what the early data shows. Use this to build the internal case for Phase 2.",
      needsPrep[0] ? `Begin data preparation for ${needsPrep[0].name}. Audit current data sources and map integration points.` : "Expand the first live workflow to additional team members or locations.",
      "Assign KPIs to each active workflow. Add a monthly AI performance review to the leadership agenda. This systematically closes the Measurement & Value gap.",
    ],
    phase3: [
      needsPrep[0] ? `Launch ${needsPrep[0].name} once data preparation is complete. This is typically the highest-volume opportunity and generates the clearest ROI data.` : "Begin scoping the next workflow for implementation.",
      blocked[0] ? `Begin ${blocked[0].name} scoping. Engage ${blocked[0].owner || "the relevant owner"} to document the current manual process — this is the prerequisite for the build.` : "Review pipeline for Phase 2 workflow candidates.",
      "Conduct a 90-day retrospective. Compare staff hours, error rates, and key metrics against Day 1 baselines. Use this data as the foundation for the Phase 2 investment decision.",
    ],
  };
};

const wfDescription = (w) => {
  const suffix = {
    "Ready":      `This workflow is ready to deploy with a ${w.build_type || "ready-built agent"} — no infrastructure prerequisites required. Your team retains full oversight.`,
    "Needs Prep": "Preparation is required before deployment — this should begin in parallel with your ready workflows to minimize delay.",
    "Blocked":    "This workflow is currently blocked by a prerequisite condition. Address this alongside your first deployment cycle to stay on timeline.",
  };
  return `${w.description || ""} ${suffix[w.status] || ""}`.trim();
};

// Normalizers — accept current backend shapes
const normalizePillars = (result) => {
  // Backend saves to `readiness_scores_by_pillar`. Each entry has pillar/label/score/band.
  // Filter OUT growth_engine — it's surfaced in Section 3 separately.
  const raw = result?.readiness_scores_by_pillar || result?.readiness_pillars || result?.pillars || result?.pillar_scores || [];
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((p) => (p.pillar || p.name || p.label) !== "growth_engine")
    .map((p) => ({
      key: p.pillar || p.name || p.label,
      name: p.label || p.name || p.pillar,
      score: Number(p.score ?? p.value ?? 0),
      band: p.band || "",
    }))
    .filter((p) => p.name);
};

const titleCaseStatus = (s) => {
  const v = String(s || "").toLowerCase();
  if (v.includes("ready")) return "Ready";
  if (v.includes("block")) return "Blocked";
  if (v.includes("prep") || v.includes("needs")) return "Needs Prep";
  if (v.includes("defer")) return "Blocked";
  return "Needs Prep";
};

const normalizeWorkflows = (result) => {
  // Prefer the rich `implementation_plan.workflow_plans` (contains prose, pain points, KPIs).
  // Fall back to `workflow_shortlist` (scored), then any legacy shapes.
  const plan = result?.implementation_plan || {};
  const planWfs = Array.isArray(plan.workflow_plans) ? plan.workflow_plans : [];
  const rawList = planWfs.length ? planWfs : (result?.workflow_shortlist || result?.recommended_workflows || result?.workflows || []);
  if (!Array.isArray(rawList) || rawList.length === 0) return [];
  return rawList.map((w, i) => {
    const status = titleCaseStatus(w.readiness_status || w.status || (w.readiness_label));
    const isCustom = String(w.delivery_method || w.build_type || "").toLowerCase().includes("custom");
    const isOrch = String(w.delivery_method || "").toLowerCase().includes("openclaw") || String(w.delivery_method || "").toLowerCase().includes("orchestration");
    const buildLabel = isCustom ? "Custom Build" : (isOrch ? "OpenClaw Orchestration" : "Ready-Built Agent");
    return {
      id: w.id || w.workflow_id || `wf_${i + 1}`,
      name: w.workflow_name || w.name || w.title || `Workflow ${i + 1}`,
      department: w.department || w.dept || w.business_area || w.category || "Operations",
      owner: w.business_owner || w.owner || w.assigned_owner || "",
      current_state: w.current_state_summary || "",
      pain_points: Array.isArray(w.main_pain_points) ? w.main_pain_points : [],
      why_ai: w.why_ai_fits || w.explanation || w.relevance_to_answers || "",
      baseline_kpi: w.baseline_kpi || "",
      kpi_target: w.kpi_target || "",
      description: w.description || w.relevance_to_answers || w.why_ai_fits || w.current_state_summary || w.summary || "",
      status,
      build_type: buildLabel,
      ai_priority: w.ai_priority || w.priority || (i + 1),
    };
  });
};

const extractGrowthEngine = (result) => {
  // Growth Engine is saved as a pillar entry with pillar='growth_engine' on 0-5 scale.
  const raw = result?.readiness_scores_by_pillar || [];
  const ge = Array.isArray(raw) ? raw.find((p) => (p.pillar || p.name || p.label) === "growth_engine") : null;
  if (ge) return Number(ge.score || 0);
  return Number(result?.growth_engine_score || result?.opportunity_score || 0);
};

// ── Component ──────────────────────────────────────

export default function CEOReportV2({ result, prospectInfo, tenantId, onConfirmScope, portalMode = false, sowApproved = false, onChooseWorkflows, initialMode = false, onUpgradeCTA }) {
  const [activeAnchor, setActiveAnchor] = useState("verdict");
  const [confirming, setConfirming] = useState(false);
  const [showUpdateBanner, setShowUpdateBanner] = useState(false);
  const navigate = useNavigate();

  // Inject styles into document head once — avoids JSX <style> ordering quirks
  useEffect(() => {
    const id = "ceo-report-v2-styles";
    if (document.getElementById(id)) return;
    const tag = document.createElement("style");
    tag.id = id;
    tag.textContent = CSS;
    document.head.appendChild(tag);
  }, []);

  // "Updates since last visit" — customer-side only
  useEffect(() => {
    if (!portalMode || !result?.id) return;
    try {
      const key = `589labs:portal:lastViewed:${tenantId || "global"}:${result.id}`;
      const prev = localStorage.getItem(key);
      const updatedAt = new Date(result.updated_at || result.created_at || 0).getTime();
      if (prev) {
        const prevMs = new Date(prev).getTime();
        if (updatedAt > prevMs + 60000) {
          setShowUpdateBanner(true);
        }
      }
      // Stamp now so subsequent visits get a fresh delta baseline
      localStorage.setItem(key, new Date().toISOString());
    } catch {
      // ignore
    }
  }, [portalMode, tenantId, result?.id, result?.updated_at, result?.created_at]);

  const refs = {
    verdict: useRef(null),
    readiness: useRef(null),
    engine: useRef(null),
    workflows: useRef(null),
    actions: useRef(null),
  };

  // Active-anchor on scroll
  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY + 120;
      let active = "verdict";
      Object.entries(refs).forEach(([key, ref]) => {
        if (ref.current && ref.current.offsetTop <= y) active = key;
      });
      setActiveAnchor(active);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const scrollTo = (anchor) => {
    const el = refs[anchor]?.current;
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const pillars = useMemo(() => normalizePillars(result), [result]);
  const workflows = useMemo(() => normalizeWorkflows(result), [result]);
  const overallScore = Number(result?.overall_readiness_score || result?.overall_score || 0);
  const geScore = useMemo(() => extractGrowthEngine(result), [result]);
  const gatingFlags = result?.gating_flags || result?.flags || [];
  const orgName = prospectInfo?.company_name || result?.org_name || result?.company_name || "Your Organization";
  const generatedDate = new Date(result?.created_at || Date.now()).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });

  const plan = result?.implementation_plan || {};
  const execSummary = plan.executive_summary || {};
  const roadmap = plan.roadmap_30_60_90 || {};

  const lowestPillar = useMemo(() => [...pillars].sort((a, b) => a.score - b.score)[0] || { name: "—", score: 0 }, [pillars]);
  const highestPillar = useMemo(() => [...pillars].sort((a, b) => b.score - a.score)[0] || { name: "—", score: 0 }, [pillars]);
  const sortedPillars = useMemo(() => [...pillars].sort((a, b) => a.score - b.score), [pillars]);
  const readyWfs = useMemo(() => workflows.filter((w) => w.status === "Ready"), [workflows]);

  // Section 1 — prefer AI-generated executive summary, else template verdict
  const aiBlockers = Array.isArray(execSummary.top_blockers) ? execSummary.top_blockers : [];
  const aiOpps = Array.isArray(execSummary.top_opportunities) ? execSummary.top_opportunities : [];
  const blockers = useMemo(() => {
    if (aiBlockers.length) {
      return aiBlockers.slice(0, 2).map((b) => {
        const s = String(b);
        const [title, ...rest] = s.split(/[—–-]/);
        return { icon: "⚠", label: "Critical Gap", title: title.trim(), description: rest.join("—").trim() || s };
      });
    }
    return sortedPillars.slice(0, 2).map((p) => ({ icon: "⚠", label: "Critical Gap", title: p.name, description: PILLAR_EXPLAIN(p.name) }));
  }, [aiBlockers, sortedPillars]);
  const opportunities = useMemo(() => {
    if (aiOpps.length) {
      return aiOpps.slice(0, 2).map((o) => {
        const s = String(o);
        const [title, ...rest] = s.split(/[—–-]/);
        return { icon: "✓", label: "Ready Now", title: title.trim(), description: rest.join("—").trim() || s };
      });
    }
    return readyWfs.slice(0, 2).map((w) => ({ icon: "✓", label: "Ready Now", title: w.name, description: w.description || w.why_ai || "Deployment-ready opportunity." }));
  }, [aiOpps, readyWfs]);
  const verdictText = useMemo(() => {
    if (execSummary.summary_text && String(execSummary.summary_text).trim().length > 80) {
      return execSummary.summary_text;
    }
    return generateVerdict(orgName, overallScore.toFixed(2), lowestPillar, readyWfs);
  }, [execSummary, orgName, overallScore, lowestPillar, readyWfs]);

  // Section 5 — prefer AI-generated roadmap, else template actions
  const actions = useMemo(() => {
    const toLines = (arr) => (Array.isArray(arr) ? arr.filter(Boolean).map((r) => {
      if (typeof r === "string") return r;
      const task = r.task || r.title || "";
      const owner = r.owner ? ` — Owner: ${r.owner}` : "";
      const deliv = r.deliverable ? ` → ${r.deliverable}` : "";
      return `${task}${owner}${deliv}`.trim();
    }) : []);
    const p1 = toLines(roadmap.days_0_30);
    const p2 = toLines(roadmap.days_31_60);
    const p3 = toLines(roadmap.days_61_90);
    if (p1.length && p2.length && p3.length) {
      return { phase1: p1.slice(0, 4), phase2: p2.slice(0, 4), phase3: p3.slice(0, 4) };
    }
    return generateActions(workflows, lowestPillar);
  }, [roadmap, workflows, lowestPillar]);

  const opportunityLevel = geScore >= 4 ? "Critical" : geScore >= 3 ? "Very High" : geScore >= 2 ? "High" : "Moderate";

  const kpis = [
    { label: "Biggest Gap", value: lowestPillar.name, sub: `Lowest-scoring pillar at ${lowestPillar.score}/4`, pillLabel: "Needs Attention", pillCls: "pill-red" },
    { label: "Workflows Ready Now", value: readyWfs.length, sub: `of ${workflows.length} identified — can start immediately`, pillLabel: "Ready to Launch", pillCls: "pill-green" },
    { label: "Gating Flags", value: gatingFlags.length, sub: gatingFlags.length === 0 ? "No critical blockers preventing a start" : `${gatingFlags.length} require attention`, pillLabel: gatingFlags.length === 0 ? "All Clear" : "Action Required", pillCls: gatingFlags.length === 0 ? "pill-green" : "pill-red" },
    { label: "Strongest Pillar", value: highestPillar.name, sub: `${highestPillar.score}/4 — solid foundation to build on`, pillLabel: "Good Improvement", pillCls: "pill-amber" },
    { label: "AI Opportunity Level", value: opportunityLevel, sub: "Growth engine score signals upside", pillLabel: "Act Now", pillCls: "pill-blue" },
    { label: "Workflows Identified", value: workflows.length, sub: "Shortlisted for first implementation cycle", pillLabel: "Shortlisted", pillCls: "pill-dark" },
  ];

  const handleConfirmScope = async () => {
    setConfirming(true);
    try {
      const top3 = [...workflows].sort((a, b) => (a.ai_priority || 99) - (b.ai_priority || 99)).slice(0, 3);
      if (portalMode && onChooseWorkflows) {
        await onChooseWorkflows(top3);
      } else if (onConfirmScope) {
        await onConfirmScope(top3);
      } else if (tenantId) {
        // Fallback navigation if no handler provided
        navigate(`/portal/${tenantId}`);
      }
    } finally {
      setConfirming(false);
    }
  };

  const overallVerdictPill = overallScore >= 2.5 ? "Strong Foundation" : overallScore >= 2.0 ? "Good Improvement Opportunity" : "Significant Improvement Needed";

  const implementationUnlocked = result?.implementation_unlocked === true || (portalMode && sowApproved);

  return (
    <div className="ceo2" data-testid="ceo-report-v2">
      {/* Updates since last visit banner — customer portal only */}
      {portalMode && showUpdateBanner && (
        <div
          className="updates-banner"
          data-testid="ceo2-updates-banner"
          style={{ background: "var(--blue-light)", border: "1px solid rgba(59,79,228,0.25)", borderRadius: "var(--radius)", padding: "14px 18px", marginBottom: 20, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}
        >
          <div style={{ fontSize: 13, color: "var(--ink2)", lineHeight: 1.5 }}>
            <strong style={{ color: "var(--blue-dark)" }}>Your report was updated</strong> since your last visit — refreshed workflow analysis, priority actions, and KPIs.
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              type="button"
              onClick={() => {
                const el = refs.workflows?.current;
                if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                setShowUpdateBanner(false);
              }}
              data-testid="ceo2-updates-review-btn"
              style={{ padding: "6px 12px", background: "var(--blue)", color: "white", border: "none", borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: "pointer" }}
            >
              Review changes →
            </button>
            <button
              type="button"
              onClick={() => setShowUpdateBanner(false)}
              data-testid="ceo2-updates-dismiss-btn"
              style={{ padding: "6px 10px", background: "transparent", color: "var(--ink3)", border: "none", fontSize: 12, cursor: "pointer" }}
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* Anchor Nav */}
      <nav className="anchor-nav" data-testid="ceo2-anchor-nav">
        {[
          { key: "verdict", label: "Summary & Verdict" },
          { key: "readiness", label: "Readiness by Pillar" },
          { key: "engine", label: "AI Opportunity Size" },
          { key: "workflows", label: "Your AI Workflow Plan" },
          { key: "actions", label: "Your Priority Action Plan" },
        ].map((a) => (
          <button
            key={a.key}
            type="button"
            onClick={() => scrollTo(a.key)}
            className={`anchor-link ${activeAnchor === a.key ? "active" : ""}`}
            data-testid={`anchor-${a.key}`}
          >
            {a.label}
          </button>
        ))}
      </nav>

      {/* Hero */}
      <div className="hero">
        <div className="hero-eyebrow">{initialMode ? "Initial AI Readiness Report" : (portalMode ? "Your AI Readiness Report" : "Detailed AI Readiness Report")}</div>
        <div className="hero-title">{orgName} &mdash; AI Readiness Assessment</div>
        <div className="hero-meta">Generated {generatedDate}</div>
        <div className="score-band">
          <div className="score-primary">
            <div>
              <span className="score-num">{overallScore.toFixed(2)}</span>
              <span className="score-denom"> /4.0</span>
            </div>
            <div className="score-label">Overall Readiness Score</div>
            <span className="score-verdict">{overallVerdictPill}</span>
          </div>
          <div className="score-kpis">
            {kpis.map((k, i) => (
              <div className="kpi-card" key={i} data-testid={`ceo2-kpi-${i}`}>
                <div className="kpi-label">{k.label}</div>
                <div className="kpi-value">{k.value}</div>
                <div className="kpi-sub">{k.sub}</div>
                <span className={`kpi-pill ${k.pillCls}`}>{k.pillLabel}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Section 01 - Verdict */}
      <div className="section" id="verdict" ref={refs.verdict}>
        <div className="section-eyebrow">Section 01</div>
        <div className="section-title">Summary &amp; Verdict</div>
        <div className="section-sub">The bottom line — what your assessment means for your next 90 days.</div>

        <div className="verdict-block">
          <div className="verdict-title">The Bottom Line</div>
          <div className="verdict-body" data-testid="ceo2-verdict-body">{verdictText}</div>
        </div>

        <div className="signal-grid">
          {blockers.slice(0, 2).map((b, i) => (
            <div className="signal-card sc-red" key={`blk-${i}`}>
              <div className="signal-icon">{b.icon || "⚠"}</div>
              <div className="signal-eyebrow">{b.label || "Critical Gap"}</div>
              <div className="signal-title">{b.title || b.name}</div>
              <div className="signal-desc">{b.description || b.detail}</div>
            </div>
          ))}
          {opportunities.slice(0, 2).map((o, i) => (
            <div className="signal-card sc-green" key={`opp-${i}`}>
              <div className="signal-icon">{o.icon || "✓"}</div>
              <div className="signal-eyebrow">{o.label || "Ready Now"}</div>
              <div className="signal-title">{o.title || o.name}</div>
              <div className="signal-desc">{o.description || o.detail}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Section 02 - Readiness by Pillar */}
      <div className="section" id="readiness" ref={refs.readiness}>
        <div className="section-eyebrow">Section 02</div>
        <div className="section-title">Readiness by Pillar</div>
        <div className="section-sub">How each dimension of your organization scored, ranked from most urgent to strongest.</div>

        <div className="pillar-table">
          <div className="pillar-row pillar-row-head">
            <div className="pillar-col-label">Pillar</div>
            <div className="pillar-col-label">What this measures</div>
            <div className="pillar-col-label" style={{ textAlign: "right" }}>Score</div>
            <div className="pillar-col-label" style={{ textAlign: "right" }}>Priority</div>
          </div>
          {sortedPillars.map((p, i) => {
            const pp = priorityPill(p.score);
            return (
              <div className="pillar-row" key={p.name + i} data-testid={`ceo2-pillar-${i}`}>
                <div>
                  <div className="pillar-name">{p.name}</div>
                  <div className="pillar-so-what">{PILLAR_SO_WHAT(p.name, p.score)}</div>
                </div>
                <div>
                  <div className="pillar-bar-shell">
                    <div className="pillar-bar-fill" style={{ width: `${(p.score / 4) * 100}%`, background: barColor(p.score) }} />
                  </div>
                  <div className="pillar-explain">{PILLAR_EXPLAIN(p.name)}</div>
                </div>
                <div className="pillar-score">{p.score.toFixed(2)} <span>/4</span></div>
                <div className="pillar-status"><span className={`kpi-pill ${pp.cls}`}>{pp.label}</span></div>
              </div>
            );
          })}
        </div>

        {highestPillar.name !== "—" && (
          <div className="insight">
            <strong>{highestPillar.name}</strong> is your strongest asset at {highestPillar.score}/4 — the foundation you can lean on while you close the {lowestPillar.name} gap. Strong scores here mean less remediation work and a faster path to your first wins.
          </div>
        )}
      </div>

      {/* Section 03 - Your AI Opportunity Size */}
      <div className="section" id="engine" ref={refs.engine}>
        <div className="section-eyebrow">Section 03</div>
        <div className="section-title">Your AI Opportunity Size</div>
        <div className="section-sub">How much upside AI can realistically create for your organization — and how urgently to act.</div>

        <div className="ge-band">
          <div className="ge-cell">
            <div className="ge-cell-label">Opportunity Score</div>
            <div className="ge-num">{geScore.toFixed(1)} <span style={{ fontSize: "16px", opacity: 0.4 }}>/5</span></div>
            <span className={`kpi-pill ${geScore >= 3 ? "pill-amber" : "pill-blue"}`}>{geScore >= 4 ? "High Priority" : geScore >= 3 ? "Moderate Priority" : geScore >= 2 ? "Monitor & Accelerate" : "Relatively Mature"}</span>
            <div className="ge-interp" style={{ marginTop: 12 }}>
              Your organization sits at the {geScore >= 3 ? '"high priority" tier' : geScore >= 2 ? 'threshold between "monitor" and "high priority"' : '"relatively mature" tier'}. {geScore >= 2 ? "The inefficiencies AI addresses here are real and significant — manual data entry, disconnected systems, repetitive staff tasks. That's the right moment to act." : "Most foundational systems are already optimized — focus on targeted automations for the remaining repetitive work."}
            </div>
          </div>
          <div className="ge-cell">
            <div className="ge-cell-label">Gating Flags</div>
            <div className="ge-num" style={{ color: gatingFlags.length === 0 ? "var(--green)" : "var(--red)" }}>{gatingFlags.length}</div>
            <span className={`kpi-pill ${gatingFlags.length === 0 ? "pill-green" : "pill-red"}`}>{gatingFlags.length === 0 ? "No Critical Blockers" : `Action Required: ${gatingFlags.length} flag${gatingFlags.length === 1 ? "" : "s"}`}</span>
            <div className="ge-interp" style={{ marginTop: 12 }}>
              {gatingFlags.length === 0
                ? "Zero gating flags means there are no organizational, regulatory, or technical conditions that should prevent you from starting AI implementation today. This is a green light — not a yellow one."
                : "Address these conditions before scaling AI deployment. Each represents a prerequisite that must be met to safely move forward."}
            </div>
          </div>
          <div className="ge-cell">
            <div className="ge-cell-label">What &ldquo;{geScore >= 4 ? "High Priority" : geScore >= 3 ? "Moderate" : geScore >= 2 ? "Monitor" : "Mature"}&rdquo; Actually Means</div>
            <div className="ge-interp" style={{ marginTop: 8 }}>
              The Growth Engine score is intentionally inverted: <strong>higher scores indicate more opportunity for AI improvement</strong>, not more maturity. A score of {geScore.toFixed(1)}/5 means your organization has {geScore >= 3 ? "substantial" : geScore >= 2 ? "substantial, addressable" : "limited"} {geScore < 2 ? "addressable inefficiency" : "inefficiency"}. A score of 1.0 would indicate a highly optimized operation with little left for AI to improve. You have real room to gain.
            </div>
          </div>
        </div>

        <div className="scale-legend">
          <div className="scale-tile" style={{ borderLeft: "3px solid var(--red)" }}><strong style={{ color: "var(--red)" }}>4.0–5.0</strong>High Priority — urgent intervention needed</div>
          <div className="scale-tile" style={{ borderLeft: "3px solid var(--amber)" }}><strong style={{ color: "var(--amber)" }}>3.0–3.9</strong>Moderate Priority — strong case for AI</div>
          <div className="scale-tile" style={{ borderLeft: "3px solid var(--gold)" }}><strong style={{ color: "var(--gold)" }}>2.0–2.9</strong>Monitor — meaningful opportunity, start now</div>
          <div className="scale-tile" style={{ borderLeft: "3px solid var(--green)" }}><strong style={{ color: "var(--green)" }}>1.0–1.9</strong>Relatively Mature — optimize existing workflows</div>
        </div>
      </div>

      {/* Section 04 - Your AI Workflow Plan */}
      <div className="section" id="workflows" ref={refs.workflows}>
        <div className="section-eyebrow">Section 04</div>
        <div className="section-title">Your AI Workflow Plan</div>
        <div className="section-sub">{workflows.length > 0 ? `${workflows.length} specific workflow${workflows.length === 1 ? "" : "s"} identified for your organization — ranked by readiness and impact. Start with the green ones.` : "The workflows that will deliver the most value, ranked by readiness and impact."}</div>

        <div className="wf-list">
          {workflows.length === 0 && (
            <div className="locked-card" style={{ textAlign: "center" }}>
              <div className="locked-body">No workflows have been recommended yet. Generate the workflow shortlist to see your AI Workflow Plan.</div>
            </div>
          )}
          {workflows.sort((a, b) => (a.ai_priority || 99) - (b.ai_priority || 99)).map((w, i) => {
            const statusCls = w.status === "Ready" ? "wb-ready" : w.status === "Blocked" ? "wb-blocked" : "wb-needs";
            const buildCls = (w.build_type || "").toLowerCase().includes("custom") ? "wb-custom" : "wb-agent";
            const bodyText = w.why_ai || w.description || wfDescription(w);
            return (
              <div className={`wf-item ${w.status === "Ready" ? "wf-ready-card" : ""}`} key={w.id} data-testid={`ceo2-wf-${i}`}>
                <div className="wf-num">{i + 1}</div>
                <div className="wf-body">
                  <div className="wf-name">{w.name}</div>
                  <div className="wf-meta-row">{w.department}{w.owner ? ` · Owner: ${w.owner}` : ""}</div>
                  <div className="wf-desc">{bodyText}</div>
                  {w.current_state && (
                    <div className="wf-desc" style={{ marginTop: 8, color: "var(--ink3)" }}><em>Today:</em> {w.current_state}</div>
                  )}
                  {w.pain_points && w.pain_points.length > 0 && (
                    <ul style={{ margin: "8px 0 0 16px", padding: 0, fontSize: 12, color: "var(--ink3)", lineHeight: 1.55 }}>
                      {w.pain_points.slice(0, 3).map((pp, pi) => (
                        <li key={pi}>{pp}</li>
                      ))}
                    </ul>
                  )}
                  {(w.baseline_kpi || w.kpi_target) && (
                    <div style={{ marginTop: 10, fontSize: 12, color: "var(--ink2)" }}>
                      {w.baseline_kpi && <><strong>Baseline:</strong> {w.baseline_kpi}&nbsp;&nbsp;</>}
                      {w.kpi_target && <><strong>Target:</strong> {w.kpi_target}</>}
                    </div>
                  )}
                </div>
                <div className="wf-badges">
                  <span className={statusCls}>{w.status === "Ready" ? "Ready Now" : w.status === "Blocked" ? "Blocked — Prep Required" : "Needs Prep"}</span>
                  <span className={buildCls}>{w.build_type || "Ready-Built Agent"}</span>
                </div>
              </div>
            );
          })}
        </div>

        {readyWfs.length > 0 && (
          <div className="insight">
            <strong>{readyWfs.length} workflow{readyWfs.length === 1 ? "" : "s"} ready to start immediately.</strong> These can be deployed without infrastructure changes, beginning Day 1 of implementation: {readyWfs.slice(0, 3).map((w) => w.name).join(", ")}.
          </div>
        )}
      </div>

      {/* Section 05 - Your Priority Action Plan */}
      <div className="section" id="actions" ref={refs.actions}>
        <div className="section-eyebrow">Section 05</div>
        <div className="section-title">Your Priority Action Plan</div>
        <div className="section-sub">What to do, in what order, over the next 90 days. Concrete steps — not abstract recommendations.</div>

        <div className="timeline">
          {[
            { rail: "Days 1–30", title: "Launch & Align", actions: actions.phase1 },
            { rail: "Days 31–60", title: "Measure & Prepare", actions: actions.phase2 },
            { rail: "Days 61–90", title: "Scale & Expand", actions: actions.phase3 },
          ].map((phase, p) => (
            <div className="tl-row" key={p}>
              <div className="tl-rail">
                {phase.rail}
                <div className="tl-dot" />
              </div>
              <div className="tl-actions">
                <div style={{ fontWeight: 700, marginBottom: 6, color: "var(--ink2)", fontSize: 14 }}>{phase.title}</div>
                {phase.actions.map((a, ai) => (
                  <div className="tl-action" key={ai}>
                    <div className="tl-num">{ai + 1}</div>
                    <div className="tl-text">{a}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="next-step-cta" data-testid="ceo2-confirm-cta">
          <div>
            <div className="ns-eyebrow">{portalMode ? "Your Next Step" : "Recommended Next Step"}</div>
            <div className="ns-title">{initialMode ? "Unlock Your Detailed AI Readiness Assessment" : (portalMode ? "Choose Your Top 3 Workflows" : "Confirm Your Scope & Begin Implementation")}</div>
            <div className="ns-body">
              {initialMode
                ? "Your Initial Readiness snapshot is complete. The In-Depth Assessment goes 3× deeper — pillar-level analysis, a ranked AI Workflow Plan tailored to your business, a 30/60/90-day roadmap, and an AI Delivery Standard your team can follow from day one."
                : portalMode
                ? `We've identified ${workflows.length} opportunit${workflows.length === 1 ? "y" : "ies"} for your organization — ${readyWfs.length} ready to deploy now. Select your top 3 to lock scope and move into the Implementation Project ($9,999) — the fastest path from assessment to live deployment.`
                : `You have ${readyWfs.length} ready-to-deploy workflow${readyWfs.length === 1 ? "" : "s"} and ${workflows.length} total opportunit${workflows.length === 1 ? "y" : "ies"} on your shortlist. Confirm scope to lock your top 3 selections and unlock the $9,999 Implementation Project — the fastest path from assessment to live deployment.`}
            </div>
          </div>
          <button
            type="button"
            onClick={initialMode ? (onUpgradeCTA || (() => {})) : handleConfirmScope}
            disabled={initialMode ? false : (confirming || workflows.length === 0)}
            className="ns-btn"
            data-testid="ceo2-confirm-scope-btn"
          >
            {initialMode ? "Purchase In-Depth Assessment →" : (confirming ? "Saving…" : (portalMode ? "Choose Your Top 3 Workflows →" : "Confirm Scope & Start →"))}
          </button>
        </div>
      </div>

      {/* Locked upsell section — hidden when portal customer has already approved scope.
          D-02/D-06 (May-2026): also hidden in initialMode and portalMode to keep
          a single authoritative purchase / workflow-selection container per page. */}
      {!implementationUnlocked && !initialMode && !(portalMode) && !(portalMode && sowApproved) && (
        <div className="locked-card" data-testid="ceo2-locked-section">
          <div className="locked-icon"><Lock className="inline" size={28} /></div>
          <div className="locked-title">
            {initialMode ? "Your AI Workflow Plan, 30/60/90 Roadmap, and AI Delivery Standard are Locked" : "Your 30/60/90-Day Roadmap and AI Delivery Standard are Locked"}
          </div>
          <div className="locked-body">
            {initialMode
              ? "These unlock after the Detailed AI Readiness Assessment — including your ranked AI Workflow Plan, sprint-level implementation roadmap, governance standards, and integration specifications."
              : portalMode
              ? "Detailed sprint plans, integration specifications, and your AI Delivery Standard unlock after you confirm scope and purchase the Implementation Project ($9,999)."
              : "Detailed milestone plans and your AI Delivery Standard unlock after the Implementation Project ($9,999) is purchased. They include sprint plans, integration specifications, deliverable timelines, and your enterprise AI standards."}
          </div>
          <button
            type="button"
            className="locked-btn"
            onClick={initialMode ? (onUpgradeCTA || (() => {})) : handleConfirmScope}
          >
            {initialMode ? "Purchase In-Depth Assessment →" : (portalMode ? "Choose Your Top 3 Workflows →" : "Unlock Full Implementation Plan →")}
          </button>
        </div>
      )}
    </div>
  );
}
